import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import Edit from 'assets/icons/EditBlue.png'
import Delete from 'assets/icons/Delete.png'
import { useState } from 'react'
import { EMAIL_TYPES } from 'pages/address/address.constant'
import { contactAddFieldSx } from '../customer-detail-fields'
import {
  useDeleteCompanyEmailMutation,
  useGetCompanyEmailsQuery,
  useUpdateCompanyEmailMutation,
} from 'store/company/company.api'
import { CompanyAssociation, CompanyEmail, EmailType } from 'store/company/company.types'

interface CustomerEmailTabProps {
  association: CompanyAssociation
}

type EmailEditDraft = {
  contactName: string
  email: string
  emailType: EmailType
}

const CustomerEmailTab = ({ association }: CustomerEmailTabProps) => {
  const companyId = association.companyId
  const { data: emails = [], isLoading } = useGetCompanyEmailsQuery({
    companyId,
  })
  const [deleteEmail, { isLoading: isDeleting }] = useDeleteCompanyEmailMutation()
  const [updateEmail, { isLoading: isUpdating }] = useUpdateCompanyEmailMutation()
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState<EmailEditDraft | null>(null)

  const handleDelete = async (companyEmailId: string) => {
    if (emails.length <= 1) {
      setError("Can't delete all emails. At least one email is required.")
      return
    }
    setError(null)
    try {
      await deleteEmail({ companyEmailId }).unwrap()
    } catch (e) {
      console.error('Failed to delete email', e)
    }
  }

  const handleEdit = (row: CompanyEmail) => {
    setEditingId(row.companyEmailId)
    setEditDraft({
      contactName: row.contactName ?? '',
      email: row.email,
      emailType: row.emailType,
    })
    setError(null)
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditDraft(null)
  }

  const handleSaveEdit = async (companyEmailId: string) => {
    if (!editDraft?.email.trim()) return
    try {
      await updateEmail({
        companyEmailId,
        body: {
          email: editDraft.email.trim(),
          emailType: editDraft.emailType,
          contactName: editDraft.contactName.trim(),
        },
      }).unwrap()
      setEditingId(null)
      setEditDraft(null)
    } catch (e) {
      console.error('Failed to update email', e)
    }
  }

  const formatEmailType = (t: EmailType) =>
    EMAIL_TYPES.find((e) => e.value === t)?.label ?? t

  const isRowBusy = isDeleting || isUpdating

  return (
    <Box sx={{ px: 3, py: 2 }}>
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {isLoading ? (
        <Typography variant="body2">Loading…</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Contact Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emails.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No emails yet.
                </TableCell>
              </TableRow>
            ) : (
              emails.map((row) => {
                const isEditing = editingId === row.companyEmailId

                return (
                  <TableRow key={row.companyEmailId}>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editDraft?.contactName ?? ''}
                          onChange={(e) =>
                            setEditDraft((prev) =>
                              prev ? { ...prev, contactName: e.target.value } : prev
                            )
                          }
                          sx={contactAddFieldSx}
                        />
                      ) : (
                        row.contactName || '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="email"
                          value={editDraft?.email ?? ''}
                          onChange={(e) =>
                            setEditDraft((prev) =>
                              prev ? { ...prev, email: e.target.value } : prev
                            )
                          }
                          sx={contactAddFieldSx}
                        />
                      ) : (
                        row.email
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editDraft?.emailType ?? row.emailType}
                            onChange={(e) =>
                              setEditDraft((prev) =>
                                prev
                                  ? { ...prev, emailType: e.target.value as EmailType }
                                  : prev
                              )
                            }
                            sx={contactAddFieldSx}
                          >
                            {EMAIL_TYPES.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        formatEmailType(row.emailType)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {isEditing ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleSaveEdit(row.companyEmailId)}
                            disabled={isRowBusy || !editDraft?.email.trim()}
                            aria-label="Save email"
                          >
                            <CheckIcon fontSize="small" color="primary" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                            disabled={isRowBusy}
                            aria-label="Cancel edit"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                            disabled={isRowBusy || editingId !== null}
                            aria-label="Edit email"
                          >
                            <img
                              src={Edit}
                              alt="Edit"
                              width={24}
                              height={24}
                              style={{ cursor: 'pointer' }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.companyEmailId)}
                            disabled={isRowBusy || editingId !== null}
                            aria-label="Delete email"
                          >
                            <img
                              src={Delete}
                              alt="Delete"
                              width={20}
                              height={22}
                              style={{ cursor: 'pointer' }}
                            />
                          </IconButton>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}

export default CustomerEmailTab
